import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UsersThree, Plus, Trash, PencilSimple, UserPlus, X } from "@phosphor-icons/react";

export default function TeamsPage() {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [form, setForm] = useState({ name: "", sector: "", description: "" });
  const [memberForm, setMemberForm] = useState({ user_id: "", team_role: "finder" });

  const SECTORS = ["Education", "Healthcare", "Finance", "IT & Technology", "Manufacturing", "Retail", "Legal", "Construction", "Energy", "Other"];

  useEffect(() => { loadTeams(); loadUsers(); }, []);

  const loadTeams = async () => {
    try {
      const { data } = await api.get("/teams");
      setTeams(data);
    } catch { toast.error("Failed to load teams"); }
    finally { setLoading(false); }
  };

  const loadUsers = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); } catch {}
  };

  const loadTeamDetail = async (teamId) => {
    try {
      const { data } = await api.get(`/teams/${teamId}`);
      setShowDetail(data);
    } catch { toast.error("Failed to load team details"); }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Team name is required"); return; }
    try {
      await api.post("/teams", form);
      toast.success("Team created");
      setShowCreate(false);
      setForm({ name: "", sector: "", description: "" });
      loadTeams();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create team"); }
  };

  const handleDelete = async (teamId) => {
    if (!confirm("Delete this team?")) return;
    try {
      await api.delete(`/teams/${teamId}`);
      toast.success("Team deleted");
      setShowDetail(null);
      loadTeams();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete team"); }
  };

  const handleAddMember = async () => {
    if (!memberForm.user_id) { toast.error("Select a user"); return; }
    try {
      await api.post(`/teams/${showDetail.id}/members`, memberForm);
      toast.success("Member added");
      setShowAddMember(false);
      setMemberForm({ user_id: "", team_role: "finder" });
      loadTeamDetail(showDetail.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add member"); }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await api.delete(`/teams/${showDetail.id}/members/${userId}`);
      toast.success("Member removed");
      loadTeamDetail(showDetail.id);
    } catch { toast.error("Failed to remove member"); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 skeleton-shimmer rounded-lg" />)}</div>;

  return (
    <div data-testid="teams-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Teams</h1>
          <p className="text-sm text-[#6B7280] mt-1">Manage recruitment teams by sector</p>
        </div>
        {user?.role === "admin" && (
          <Button onClick={() => setShowCreate(true)} className="bg-[#0F172A] hover:bg-[#1E293B] gap-2" data-testid="create-team-btn">
            <Plus size={16} /> New Team
          </Button>
        )}
      </div>

      {teams.length === 0 ? (
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center">
          <UsersThree size={48} className="text-[#D1D5DB] mx-auto mb-3" />
          <p className="text-[#6B7280]">No teams created yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map(team => (
            <button key={team.id} onClick={() => loadTeamDetail(team.id)} className="bg-white rounded-lg border border-[#E5E7EB] p-5 text-left hover:shadow-md transition-shadow" data-testid={`team-card-${team.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-[#0F172A]">{team.name}</h3>
                  {team.sector && <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-semibold uppercase bg-[#F0F4FF] text-[#1E3A8A] rounded">{team.sector}</span>}
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#0F172A]/5 flex items-center justify-center">
                  <UsersThree size={20} className="text-[#0F172A]" />
                </div>
              </div>
              {team.description && <p className="text-xs text-[#6B7280] mt-2 line-clamp-2">{team.description}</p>}
              <p className="text-xs text-[#C5A059] font-medium mt-3">{team.member_count || 0} members</p>
            </button>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4" data-testid="create-team-dialog">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#0F172A]">Create Team</h2>
              <button onClick={() => setShowCreate(false)}><X size={20} className="text-[#6B7280]" /></button>
            </div>
            <div className="space-y-3">
              <Input placeholder="Team Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="team-name-input" />
              <select className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" value={form.sector} onChange={e => setForm({...form, sector: e.target.value})} data-testid="team-sector-select">
                <option value="">Select Sector</option>
                {SECTORS.map(s => <option key={s} value={s.toLowerCase()}>{s}</option>)}
              </select>
              <textarea className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm resize-none" rows={3} placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="team-description-input" />
              <Button onClick={handleCreate} className="w-full bg-[#0F172A] hover:bg-[#1E293B]" data-testid="submit-team-btn">Create Team</Button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      {showDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 mx-4 max-h-[80vh] overflow-y-auto" data-testid="team-detail-dialog">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-bold text-[#0F172A]">{showDetail.name}</h2>
                {showDetail.sector && <span className="text-xs text-[#1E3A8A] font-medium uppercase">{showDetail.sector}</span>}
              </div>
              <div className="flex gap-2">
                {user?.role === "admin" && <button onClick={() => handleDelete(showDetail.id)} className="text-red-500 hover:text-red-700"><Trash size={18} /></button>}
                <button onClick={() => setShowDetail(null)}><X size={20} className="text-[#6B7280]" /></button>
              </div>
            </div>
            {showDetail.description && <p className="text-sm text-[#6B7280] mb-4">{showDetail.description}</p>}

            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#0F172A]">Members ({showDetail.members?.length || 0})</h3>
              {user?.role === "admin" && (
                <Button variant="outline" size="sm" onClick={() => setShowAddMember(true)} className="gap-1" data-testid="add-member-btn">
                  <UserPlus size={14} /> Add
                </Button>
              )}
            </div>

            {(showDetail.members || []).length === 0 ? (
              <p className="text-sm text-[#6B7280] py-4 text-center">No members yet</p>
            ) : (
              <div className="space-y-2">
                {showDetail.members.map(m => (
                  <div key={m.id} className="flex items-center justify-between py-2 px-3 bg-[#F9FAFB] rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-[#0F172A]">{m.user_name || "Unknown"}</p>
                      <p className="text-xs text-[#6B7280]">{m.user_email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded bg-[#0F172A] text-[#C5A059]">
                        {m.team_role?.replace("_", " ")}
                      </span>
                      {user?.role === "admin" && (
                        <button onClick={() => handleRemoveMember(m.user_id)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Member Sub-dialog */}
            {showAddMember && (
              <div className="mt-4 p-4 border border-[#E5E7EB] rounded-lg bg-[#F9FAFB]">
                <h4 className="text-sm font-semibold mb-3">Add Member</h4>
                <div className="space-y-2">
                  <select className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" value={memberForm.user_id} onChange={e => setMemberForm({...memberForm, user_id: e.target.value})} data-testid="member-user-select">
                    <option value="">Select User</option>
                    {users.filter(u => u.is_active).map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                  </select>
                  <select className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" value={memberForm.team_role} onChange={e => setMemberForm({...memberForm, team_role: e.target.value})} data-testid="member-role-select">
                    <option value="team_leader">Team Leader</option>
                    <option value="administrator">Administrator</option>
                    <option value="finder">Finder</option>
                  </select>
                  <div className="flex gap-2">
                    <Button onClick={handleAddMember} size="sm" className="bg-[#0F172A]" data-testid="confirm-add-member-btn">Add</Button>
                    <Button variant="outline" size="sm" onClick={() => setShowAddMember(false)}>Cancel</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
